import { useEffect, useRef, useState, useCallback } from 'react';
import type { WSClientMessage, WSServerMessage, EventWithItems, PublicParticipant } from '@trueup/shared';

interface UseWebSocketOptions {
  onJoined?: (participant: PublicParticipant, event: EventWithItems) => void;
  onItemClaimed?: (itemId: string, claim: any, participant: PublicParticipant) => void;
  onItemUnclaimed?: (itemId: string, participantId: string) => void;
  onUnitClaimed?: (itemId: string, unitNumber: number, claim: any, participant: PublicParticipant) => void;
  onUnitUnclaimed?: (itemId: string, unitNumber: number, participantId: string) => void;
  onFractionClaimed?: (itemId: string, claim: any, participant: PublicParticipant) => void;
  onFractionUnclaimed?: (itemId: string, participantId: string) => void;
  onParticipantJoined?: (participant: PublicParticipant) => void;
  onParticipantLeft?: (participantId: string) => void;
  onEventUpdated?: (event: EventWithItems) => void;
  onError?: (message: string) => void;
  /** Idle timeout in milliseconds (default: 2 minutes) */
  idleTimeout?: number;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const idleTimeoutRef = useRef<number | null>(null);
  const wasIdleDisconnectRef = useRef(false);
  const pendingMessagesRef = useRef<WSClientMessage[]>([]);
  // Use ref to always have access to latest options (avoids stale closures)
  const optionsRef = useRef(options);
  optionsRef.current = options;
  // Store join credentials to auto-rejoin on reconnect
  const joinCredentialsRef = useRef<{ eventCode: string; name: string; sessionToken?: string } | null>(null);
  // Store event status to check if reconnection is needed
  const eventStatusRef = useRef<string | null>(null);

  const IDLE_TIMEOUT = options.idleTimeout ?? 600000; // 10 minutes default

  const resetIdleTimer = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    idleTimeoutRef.current = window.setTimeout(() => {
      // Don't idle disconnect if already disconnected
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      console.log('WebSocket: Disconnecting due to idle timeout');
      wasIdleDisconnectRef.current = true;
      wsRef.current.close();
    }, IDLE_TIMEOUT);
  }, [IDLE_TIMEOUT]);

  const connect = useCallback(() => {
    // Don't create a new WebSocket if one already exists and isn't closed
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      console.log('WebSocket already exists, readyState:', wsRef.current.readyState);
      return;
    }

    wasIdleDisconnectRef.current = false;

    // In development, connect directly to the API server
    // In production, use the same host
    const isDev = import.meta.env.DEV;
    const wsUrl = isDev
      ? 'ws://localhost:3000/ws'
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

    console.log('WebSocket connecting to:', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      setError(null);

      // Auto-rejoin if we have stored credentials
      if (joinCredentialsRef.current) {
        const { eventCode, name, sessionToken } = joinCredentialsRef.current;
        console.log('Auto-rejoining with credentials:', { eventCode, name, hasToken: !!sessionToken });
        const joinMessage: WSClientMessage = { type: 'join', eventCode, name, sessionToken };
        ws.send(JSON.stringify(joinMessage));

        // Remove any duplicate join messages from pending queue
        pendingMessagesRef.current = pendingMessagesRef.current.filter(
          (msg) => !(msg.type === 'join' && msg.eventCode === eventCode && msg.name === name)
        );
      }

      // Send any pending messages
      while (pendingMessagesRef.current.length > 0) {
        const msg = pendingMessagesRef.current.shift()!;
        console.log('Sending queued message:', msg);
        ws.send(JSON.stringify(msg));
      }

      // Start ping interval
      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);

      // Start idle timer
      resetIdleTimer();
    };

    ws.onmessage = (event) => {
      try {
        console.log('WebSocket message received:', event.data);
        const message = JSON.parse(event.data) as WSServerMessage;
        handleMessage(message);
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    ws.onerror = () => {
      setError('Connection error');
    };

    ws.onclose = (event) => {
      console.log('WebSocket onclose, code:', event.code, 'reason:', event.reason, 'wasIdle:', wasIdleDisconnectRef.current);
      setIsConnected(false);
      setIsReconnecting(false);
      wsRef.current = null;

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }

      // Don't auto-reconnect if this was an idle disconnect
      // The user must interact to trigger reconnection
      if (wasIdleDisconnectRef.current) {
        console.log('WebSocket: Not auto-reconnecting (idle disconnect)');
        return;
      }

      // Reconnect after delay
      reconnectTimeoutRef.current = window.setTimeout(() => {
        console.log('Reconnecting after close...');
        connect();
      }, 3000);
    };
  }, [resetIdleTimer]);

  const handleMessage = useCallback((message: WSServerMessage) => {
    const opts = optionsRef.current;
    switch (message.type) {
      case 'joined':
        // Track event status for reconnection logic
        eventStatusRef.current = message.event.status;
        setIsReconnecting(false);
        opts.onJoined?.(message.participant, message.event);
        break;
      case 'itemClaimed':
        opts.onItemClaimed?.(message.itemId, message.claim, message.participant);
        break;
      case 'itemUnclaimed':
        opts.onItemUnclaimed?.(message.itemId, message.participantId);
        break;
      case 'unitClaimed':
        opts.onUnitClaimed?.(message.itemId, message.unitNumber!, message.claim, message.participant);
        break;
      case 'unitUnclaimed':
        opts.onUnitUnclaimed?.(message.itemId, message.unitNumber!, message.participantId);
        break;
      case 'fractionClaimed':
        opts.onFractionClaimed?.(message.itemId, message.claim, message.participant);
        break;
      case 'fractionUnclaimed':
        opts.onFractionUnclaimed?.(message.itemId, message.participantId);
        break;
      case 'participantJoined':
        opts.onParticipantJoined?.(message.participant);
        break;
      case 'participantLeft':
        opts.onParticipantLeft?.(message.participantId);
        break;
      case 'eventUpdated':
        // Track event status for reconnection logic
        eventStatusRef.current = message.event.status;
        opts.onEventUpdated?.(message.event);
        break;
      case 'error':
        setError(message.message);
        opts.onError?.(message.message);
        break;
      case 'pong':
        // Heartbeat response
        break;
    }
  }, []);

  const send = useCallback((message: WSClientMessage) => {
    // Reset idle timer on activity
    resetIdleTimer();

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('Sending message:', message);
      wsRef.current.send(JSON.stringify(message));
    } else {
      // Queue message to be sent when connected
      console.log('Queueing message (not connected yet):', message);
      pendingMessagesRef.current.push(message);
    }
  }, [resetIdleTimer]);

  const join = useCallback((eventCode: string, name: string, sessionToken?: string) => {
    // Store credentials for auto-rejoin on reconnect
    joinCredentialsRef.current = { eventCode, name, sessionToken };
    send({ type: 'join', eventCode, name, sessionToken });
  }, [send]);

  const claim = useCallback((itemId: string, quantity?: number) => {
    send({ type: 'claim', itemId, quantity });
  }, [send]);

  const unclaim = useCallback((itemId: string) => {
    send({ type: 'unclaim', itemId });
  }, [send]);

  const claimUnit = useCallback((itemId: string, unitNumber: number) => {
    send({ type: 'claimUnit', itemId, unitNumber });
  }, [send]);

  const unclaimUnit = useCallback((itemId: string, unitNumber: number) => {
    send({ type: 'unclaimUnit', itemId, unitNumber });
  }, [send]);

  const claimFraction = useCallback((itemId: string, numerator: number, denominator: number) => {
    send({ type: 'claimFraction', itemId, numerator, denominator });
  }, [send]);

  const unclaimFraction = useCallback((itemId: string) => {
    send({ type: 'unclaimFraction', itemId });
  }, [send]);

  /** Call when user interacts with the page to reconnect if idle-disconnected and event is still active */
  const markActivity = useCallback(() => {
    // Reset idle timer if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      resetIdleTimer();
      return;
    }

    // Reconnect if we were idle-disconnected and event is still active
    if (wasIdleDisconnectRef.current && eventStatusRef.current === 'active') {
      console.log('WebSocket: Reconnecting after idle disconnect (event still active)');
      setIsReconnecting(true);
      wasIdleDisconnectRef.current = false;
      connect();
    }
  }, [connect, resetIdleTimer]);

  const disconnect = useCallback(() => {
    console.log('disconnect() called');
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }

    if (wsRef.current) {
      console.log('Closing WebSocket, readyState:', wsRef.current.readyState);
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsReconnecting(false);
    // Don't clear joinCredentialsRef on disconnect - we want to auto-rejoin on reconnect
  }, []);

  useEffect(() => {
    return () => {
      console.log('useWebSocket cleanup running');
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    join,
    claim,
    unclaim,
    claimUnit,
    unclaimUnit,
    claimFraction,
    unclaimFraction,
    markActivity,
    send,
    isConnected,
    isReconnecting,
    error,
  };
}
